import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';

export type TouchableTooltipCollectionItem = {
  key: string;
};

export type TouchableTooltipCollectionProps<
  TItem extends TouchableTooltipCollectionItem,
> = {
  items: TItem[];
  tooltipFn: (item: TItem) => React.ReactNode;
};

export type TouchableTooltipCollectionHook<
  TItem extends TouchableTooltipCollectionItem,
> = {
  renderTooltip: () => React.ReactNode;
  createTouchTarget: (params: {
    key: string;
    children: React.ReactNode;
    fallback?: React.ReactNode;
  }) => React.ReactNode;
  tooltipItem: TItem | null;
};

export const useTouchableTooltipCollection = <
  TItem extends TouchableTooltipCollectionItem,
>({
  items,
  tooltipFn,
}: TouchableTooltipCollectionProps<TItem>): TouchableTooltipCollectionHook<TItem> => {
  const [active, setActive] = useState<{
    key: string;
    element: HTMLElement;
  } | null>(null);

  const tooltipItem = useMemo(() => {
    if (!active) {
      return null;
    }
    return items.find((i) => i.key === active.key) || null;
  }, [active, items]);

  const createTouchTarget = useCallback(
    ({
      key,
      children,
      fallback,
    }: {
      key: string;
      children: React.ReactNode;
      fallback?: React.ReactNode;
    }) => {
      if (items.find((i) => i.key === key) === undefined) {
        return fallback ?? children;
      }

      return (
        <TouchTargetComponent onClick={(el) => setActive({ key, element: el })}>
          {children}
        </TouchTargetComponent>
      );
    },
    [items],
  );

  const onClose = useCallback(() => setActive(null), []);

  const renderTooltip = useCallback(() => {
    if (!active) {
      return null;
    }

    const item = items.find((i) => i.key === active.key);
    if (!item) {
      return null;
    }

    return createPortal(
      <TooltipComponent
        key={active.key}
        parentElement={active.element}
        content={tooltipFn(item)}
        onClose={onClose}
      />,
      document.body,
    );
  }, [active, items, tooltipFn, onClose]);

  return useMemo(
    () => ({ renderTooltip, createTouchTarget, tooltipItem }),
    [renderTooltip, createTouchTarget, tooltipItem],
  );
};

const PAD = 8; // viewport padding when clamping/ flipping

export const computeCoords = ({
  triggerRect,
  tooltipRect,
  positioningMode,
  windowInnerWidth,
  windowInnerHeight,
}: {
  triggerRect: {
    left: number;
    top: number;
    width: number;
    height: number;
  };
  tooltipRect: {
    left: number;
    top: number;
    width: number;
    height: number;
  };
  positioningMode: 'aside' | 'centered';
  windowInnerWidth: number;
  windowInnerHeight: number;
}): { left: string; top: string } => {
  let left = triggerRect.left + triggerRect.width;
  let top = triggerRect.top + triggerRect.height;

  if (positioningMode === 'aside') {
    // flip horizontally if overflowing right edge
    if (left + tooltipRect.width > windowInnerWidth - PAD) {
      left = triggerRect.left - tooltipRect.width;
      if (left < PAD) left = PAD;
    }

    // flip vertically if overflowing bottom edge
    if (top + tooltipRect.height > windowInnerHeight - PAD) {
      top = triggerRect.top - tooltipRect.height;
      if (top < PAD) top = PAD;
    }

    return { left: `${left}px`, top: `${top}px` };
  }

  // centered mode
  left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
  top = triggerRect.top - tooltipRect.height - PAD;

  // clamp to viewport
  if (left < PAD) left = PAD;
  if (left + tooltipRect.width > windowInnerWidth - PAD) {
    left = windowInnerWidth - PAD - tooltipRect.width;
  }
  if (top < PAD) top = triggerRect.top + triggerRect.height + PAD;

  return { left: `${left}px`, top: `${top}px` };
};

interface TooltipComponentProps {
  parentElement: HTMLElement;
  content: React.ReactNode;
  onClose: () => void;
}

export const useTooltipRepositioner = ({
  ref,
  getTriggerRectFn,
  getTriggerElementFn,
  positioningMode = 'aside',
}: {
  ref: React.RefObject<HTMLElement>;
  getTriggerRectFn: () => {
    left: number;
    top: number;
    width: number;
    height: number;
  };
  getTriggerElementFn: () => HTMLElement | null;
  positioningMode?: 'aside' | 'centered';
}) => {
  const [coords, setCoords] = useState<{ left: string; top: string }>({
    left: '0px',
    top: '0px',
  });

  const updatePosition = useCallback(() => {
    if (!ref.current) {
      return;
    }
    const triggerRect = getTriggerRectFn();
    const tooltipRect = ref.current.getBoundingClientRect();
    setCoords(
      computeCoords({
        triggerRect,
        tooltipRect,
        positioningMode,
        windowInnerWidth: window.innerWidth,
        windowInnerHeight: window.innerHeight,
      }),
    );
  }, [getTriggerRectFn, positioningMode, ref]);

  useLayoutEffect(() => {
    // Initial paint
    updatePosition();

    const opts: AddEventListenerOptions = { capture: true, passive: true };
    window.addEventListener('scroll', updatePosition, opts);
    window.addEventListener('resize', updatePosition);

    // Parent/ tooltip resize observers
    const observers: ResizeObserver[] = [];
    if ('ResizeObserver' in window) {
      const triggerElement = getTriggerElementFn();
      [triggerElement, ref.current].forEach((el) => {
        if (!el) {
          return;
        }
        const ro = new ResizeObserver(updatePosition);
        ro.observe(el);
        observers.push(ro);
      });
    }

    return () => {
      window.removeEventListener('scroll', updatePosition, opts);
      window.removeEventListener('resize', updatePosition);
      observers.forEach((ro) => ro.disconnect());
    };
  }, [updatePosition, ref, getTriggerElementFn]);

  return useMemo(() => {
    return {
      coords,
    };
  }, [coords]);
};

export const NivoSafeTooltipWrapper = ({
  children,
  pointX,
  pointY,
  containerRef,
  chartMargin,
  positioningMode = 'centered',
}: {
  children: React.ReactNode;
  pointX: number;
  pointY: number;
  containerRef: React.RefObject<HTMLElement>;
  chartMargin: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  positioningMode?: 'aside' | 'centered';
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const getTriggerRectFn = useCallback(() => {
    const containerRect = containerRef.current?.getBoundingClientRect() ?? {
      left: 0,
      top: 0,
      width: 0,
      height: 0,
    };
    const result = {
      left: pointX + containerRect.left + chartMargin.left - 10,
      top: pointY + containerRect.top + chartMargin.top - 10,
      width: 20,
      height: 20,
    };

    return result;
  }, [containerRef, pointX, chartMargin.left, chartMargin.top, pointY]);

  const getTriggerElementFn = useCallback(() => {
    return null;
  }, []);

  const { coords } = useTooltipRepositioner({
    ref,
    getTriggerRectFn,
    getTriggerElementFn,
    positioningMode,
  });

  return createPortal(
    <div
      ref={ref}
      className={'fixed z-tooltip'}
      style={{ ...coords, pointerEvents: 'none' }}
    >
      {children}
    </div>,
    document.body,
  );
};

const TooltipComponent: React.FC<TooltipComponentProps> = ({
  parentElement,
  content,
  onClose,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const getTriggerElementFn = useCallback(() => {
    return parentElement;
  }, [parentElement]);

  const getTriggerRectFn = useCallback(() => {
    return parentElement.getBoundingClientRect();
  }, [parentElement]);

  const { coords } = useTooltipRepositioner({
    ref,
    getTriggerRectFn,
    getTriggerElementFn,
  });

  useLayoutEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  useLayoutEffect(() => {
    const checkVisibility = () => {
      const rect = parentElement.getBoundingClientRect();
      const inView =
        rect.right > 0 &&
        rect.left < window.innerWidth &&
        rect.bottom > 0 &&
        rect.top < window.innerHeight;
      if (!inView) {
        onClose();
      }
    };

    const opts: AddEventListenerOptions = { capture: true, passive: true };
    window.addEventListener('scroll', checkVisibility, opts);
    window.addEventListener('resize', checkVisibility);
    return () => {
      window.removeEventListener('scroll', checkVisibility, opts);
      window.removeEventListener('resize', checkVisibility);
    };
  }, [parentElement, onClose]);

  return (
    <div className="fixed z-tooltip" style={coords}>
      <div
        ref={ref}
        className="z-tooltip bg-white rounded-lg shadow-12px p-4 min-w-[256px] max-w-[400px]"
      >
        {content}
      </div>
    </div>
  );
};

const TouchTargetComponent: React.FC<{
  onClick: (element: HTMLElement) => void;
  children: React.ReactNode;
}> = ({ onClick, children }) => {
  const ref = useRef<HTMLDivElement>(null);

  return (
    <div ref={ref} className="w-full">
      <TouchTarget
        behavior={{
          type: 'onClick',
          onClick: () => {
            if (ref.current) onClick(ref.current);
          },
        }}
        content={() => children}
      />
    </div>
  );
};
